import { HttpClient } from '@angular/common/http';
import { productDocumentation, repository } from 'pi-api';
import { Injectable, inject, DestroyRef } from '@angular/core';
import { BehaviorSubject, filter, map, of, switchMap, tap } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { takeUntilDestroyedSafe } from '../../helpers/take-until-destroyed-safe';

export enum COPY_TYPE_ENUM {
  repository,
  products,
}

export enum COPY_TARGET_TYPE {
  FILE = 1,
  FOLDER = 2,
  PRODUCT = 3,
  REPOSITORY = 4,
}

interface LastRemoteCopyRS {
  sourceType: number;
  isFileOnRoot: boolean;
}

export type CopItem = { isFolder: boolean; id: number };

export class CopyJob {
  private _isSent = false;
  private _hasFilesOnRoot = false;

  constructor(
    private readonly _items: {
      id: number;
      parents?: number[];
      isFolder: boolean;
    }[],
    private readonly _source: COPY_TYPE_ENUM
  ) {
    const minParentsLength = Math.min(...this._items.map((item) => item.parents?.length || 0));
    const rootFiles = this._items.filter((item) => {
      return (item.parents?.length || 0) === minParentsLength && !item.isFolder;
    });

    this._hasFilesOnRoot = rootFiles.length > 0;
  }

  canPaste(target: COPY_TARGET_TYPE) {
    return !(target === COPY_TARGET_TYPE.PRODUCT && this.hasFilesOnRoot);
  }

  get hasFilesOnRoot() {
    return this._hasFilesOnRoot;
  }

  get items() {
    return this._items;
  }

  get source(): COPY_TYPE_ENUM {
    return this._source;
  }

  sent() {
    if (!this._isSent) {
      this._isSent = true;
      Object.seal(this._isSent);
      return true;
    }
    return false;
  }

  copied() {
    return false;
  }
}

@Injectable({
  providedIn: 'root',
})
export class CopyPasteService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly httpClient = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private $lastCopyState: BehaviorSubject<CopyJob | null> = new BehaviorSubject<CopyJob | null>(null);

  get copyState() {
    return this.$lastCopyState.asObservable();
  }

  get canCopy() {
    return !!this.$lastCopyState.value;
  }

  canPaste(target: COPY_TARGET_TYPE) {
    return this.$lastCopyState.value?.canPaste(target) ?? true;
  }

  canPasteHere(
    targetType: COPY_TARGET_TYPE,
    targetElement?: { isLocked?: boolean; isFolder?: boolean; parentId?: number },
    parentFolderId?: number
  ) {
    const lastState = this.$lastCopyState.value;
    if (!lastState) {
      return false;
    }

    if (targetType === COPY_TARGET_TYPE.PRODUCT && lastState.items.some((e) => !e.isFolder)) {
      return false;
    }

    if (targetElement?.isFolder && !targetElement?.parentId && !parentFolderId && lastState.hasFilesOnRoot) {
      return false;
    }

    return true;
  }

  constructor() {
    this.$lastCopyState
      .asObservable()
      .pipe(
        filter((f) => !!f),
        switchMap((elem) => {
          if (elem) {
            return this.sendOverApi(elem);
          }
          return of();
        })
      )
      .subscribe();
    this.auth
      .getUser()
      .pipe(
        switchMap(() => this.getLastCopied()),
        takeUntilDestroyedSafe(this.destroyRef)
      )
      .subscribe((rs) => {
        if (rs) {
          this.$lastCopyState.next(
            new CopyJob(
              [
                {
                  id: 0,
                  isFolder: !rs.isFileOnRoot,
                },
              ],
              rs.sourceType === 1 ? COPY_TYPE_ENUM.products : COPY_TYPE_ENUM.repository
            )
          );
        }
      });
  }

  copy(items: CopItem[], from: COPY_TYPE_ENUM) {
    this.$lastCopyState.next(new CopyJob(items, from));
  }

  paste2<T = undefined>(
    targetType: COPY_TARGET_TYPE,
    entityId: number,
    targetId: number | null | undefined,
    parentId?: number,
    isRepo?: boolean
  ) {
    const value = this.$lastCopyState.value;

    if (value) {
      const url = isRepo ? repository.postPaste() : productDocumentation.postPaste();

      return this.httpClient
        .post<{ items: T[] }>(url, {
          targetType,
          entityId,
          targetId,
          parentId,
        })
        .pipe(
          tap((rs) => {
            if (rs) {
              value.copied();
            }
          }),
          map((rs) => {
            return rs.items;
          })
        );
    }

    return of();
  }

  /**
   * Stara sciezka wklejania - nieuzywana: metoda konczy sie od razu pustym strumieniem.
   * Komponenty wolaja `paste2`. Zostawiona, bo wciaz jest wolana z dwoch miejsc.
   */
  paste<T = undefined>(targetId: number, targetType: COPY_TARGET_TYPE) {
    return of<T[]>();
  }

  private sendOverApi(elem: CopyJob) {
    const url = elem.source === COPY_TYPE_ENUM.repository ? repository.postCopy() : productDocumentation.postCopy();

    const items = elem.items.filter((e) => e.id !== 0);

    if (items.length === 0) {
      return of();
    }

    return this.httpClient.post(url, elem.items).pipe(
      tap(() => {
        elem.sent();
      })
    );
  }

  private getLastCopied() {
    return this.httpClient.get<LastRemoteCopyRS>(productDocumentation.lastCopied());
  }
}
